import { jsx } from '@emotion/react/jsx-runtime';
import { createComponentWithRef, useSlot } from '@nettbureau/core';
import { useTransitionState, AppearInOut } from '@nettbureau/motion';
import { placementTransitionMap } from '@nettbureau/overlays';
import { Rectangle } from '@nettbureau/shape';
import { useCss } from '@nettbureau/styling';
import { OverlayContainer } from 'react-aria';
import { useTooltip } from './use-tooltip.js';

/**
 * Ordered list of available tooltip sizes.
 */ const tooltipSizes = [
    '2xs',
    'xs',
    'sm'
];
/**
 * Tooltips display informative text when users hover over, focus on, or tap an element.
 */ const Tooltip = createComponentWithRef((props, ref)=>{
    const [mergedProps, mergedRef] = useSlot('Tooltip', props, ref);
    const { anchorRef, children, className, css, color = 'neutral', size = 'xs', maxWidth = 60, variant = 'base' } = mergedProps;
    if (!anchorRef) throw new Error('<Tooltip> must be inside a <TooltipTrigger> or explicitly given an anchor ref.');
    const [state, { overlayProps, tooltipProps }] = useTooltip({
        ...mergedProps,
        anchorRef
    }, mergedRef);
    const [transitionState, transitionProps] = useTransitionState();
    const rectangleCss = useCss(({ mixins })=>[
            {
                wordWrap: 'break-word'
            },
            variant === 'base' && mixins.overlay()
        ]);
    if (!state.isOpen && transitionState === 'hidden') return null;
    return /*#__PURE__*/ jsx(OverlayContainer, {
        children: /*#__PURE__*/ jsx("div", {
            ...overlayProps,
            ref: mergedRef,
            children: /*#__PURE__*/ jsx(AppearInOut, {
                ...transitionProps,
                in: state.isOpen,
                from: state.placement ? placementTransitionMap[state.placement] : undefined,
                children: /*#__PURE__*/ jsx(Rectangle, {
                    "data-tooltip": true,
                    ...tooltipProps,
                    className: className,
                    css: [
                        rectangleCss,
                        css
                    ],
                    fill: variant === 'base' ? 'base' : color,
                    fillTone: variant === 'base' ? 'medium' : 'bold',
                    maxWidth: maxWidth,
                    minHeight: size,
                    outlineWidth: variant === 'base' ? 'regular' : 'none',
                    outline: color,
                    outlineTone: "medium",
                    shadow: "weaker",
                    typography: {
                        color: variant === 'base' ? color : 'neutral',
                        size
                    },
                    children: children
                })
            })
        })
    });
});

export { Tooltip, tooltipSizes };
//# sourceMappingURL=tooltip.js.map
